<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/services/interceptor.services';
import { RegulatoryDeclarationsService } from '@/services/regulatory-declarations.services';
import { ALLERGEN_META } from '@/components/product/edit/regulatory-declarations.types';
import CollapsibleSection from '@/components/product/edit/CollapsibleSection.vue';
import ProductGalleryGrid from '@/components/product/edit/ProductGalleryGrid.vue';
import type { IProductGalleryImage } from '@/types/product-image.types';

const { t } = useI18n({ useScope: 'global' });

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();
const id = computed(() => route.params.id as string);
const fromId = computed(() => route.query.from as string | undefined);
const fromTab = computed(() => route.query.tab as string | undefined);
const fromCatTab = computed(() => route.query.catTab as string | undefined);

function goBack() {
  if (fromId.value) {
    const params = new URLSearchParams();
    if (fromTab.value) params.set('tab', fromTab.value);
    if (fromCatTab.value) params.set('catTab', fromCatTab.value);
    const qs = params.toString();
    router.push(`/commerces/${fromId.value}${qs ? `?${qs}` : ''}`);
  } else router.back();
}

const {
  data: product,
  isPending,
  isError,
} = useQuery({
  queryKey: computed(() => ['product-detail', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(`products/get/${id.value}`);
    const d = r.data as any;
    return d?.data ?? d;
  },
  enabled: computed(() => !!id.value),
});

// Les groupes d'options (extras) ne sont pas inclus dans products/get/{id} —
// route dédiée, comme dans EditProductPage.vue / CommerceDetailPage.vue.
const { data: productExtras } = useQuery({
  queryKey: computed(() => ['product-extras', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(`product/${id.value}/extras`);
    const d = r.data as any;
    return (d?.data ?? d ?? []) as any[];
  },
  enabled: computed(() => !!id.value),
});

const { data: regulatoryDeclarations } = useQuery({
  queryKey: computed(() => ['product-regulatory-declarations', id.value]),
  queryFn: async () => {
    const r = await RegulatoryDeclarationsService.getRegulatoryDeclarations(
      id.value,
    );
    return r.data.data;
  },
  enabled: computed(() => !!id.value),
});

const declaredAllergens = computed(() => {
  const lines = regulatoryDeclarations.value?.declarations ?? [];
  return lines.map((l) => ({
    ...l,
    label: ALLERGEN_META[l.code].label,
  }));
});

const showDeleteModal = ref(false);
const deleting = ref(false);

async function confirmDelete() {
  if (deleting.value) return;
  deleting.value = true;
  try {
    await axiosInstance.delete(`products/delete/${id.value}`);
    queryClient.invalidateQueries({ queryKey: ['commerce-products'] });
    queryClient.invalidateQueries({ queryKey: ['commerce-products-all'] });
    queryClient.invalidateQueries({
      queryKey: ['commerce-products-assign-all'],
    });
    queryClient.invalidateQueries({ queryKey: ['commerce-products-for-opts'] });
    queryClient.invalidateQueries({ queryKey: ['commerce-org-categories'] });
    queryClient.invalidateQueries({ queryKey: ['rec-products'] });
    queryClient.invalidateQueries({ queryKey: ['rec-list'] });
    queryClient.invalidateQueries({ queryKey: ['GetProduct'] });
    goBack();
  } catch {
  } finally {
    deleting.value = false;
    showDeleteModal.value = false;
  }
}

function fmtPrice(n: string | number | null | undefined) {
  if (n == null) return '—';
  const v = typeof n === 'string' ? parseFloat(n) : n;
  return isNaN(v) ? '—' : v.toFixed(2).replace('.', ',') + ' €';
}
function fmtDate(d: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const imgUrl = computed(
  () =>
    (product.value?.image as any)?.info?.secureUrl ??
    (product.value?.image as any)?.url ??
    null,
);
const isActive = computed(() => !product.value?.disabledAt);

// La désignation/description sont renvoyées comme string simple par
// products/get, avec les traductions séparément dans designationAlternatives
// / descriptionAlternatives — pas un objet {fr,en,nl} comme ailleurs dans l'app.
const designationTranslations = computed(() => {
  const alt = product.value?.designationAlternatives;
  const d = product.value?.designation;
  if (alt) return alt;
  if (d && typeof d === 'object') return d;
  return { fr: typeof d === 'string' ? d : '' };
});
const descriptionTranslations = computed(() => {
  const alt = product.value?.descriptionAlternatives;
  const d = product.value?.description;
  if (alt) return alt;
  if (d && typeof d === 'object') return d;
  return { fr: typeof d === 'string' ? d : '' };
});

const name = computed(() => {
  const t = designationTranslations.value;
  return t.fr || t.en || t.nl || '—';
});

const desc = computed(() => {
  const t = descriptionTranslations.value;
  return t.fr || t.en || t.nl || null;
});

const categories = computed<any[]>(() =>
  (product.value?.categories ?? []).map((c: any) => c?.category ?? c),
);

const variants = computed<any[]>(
  () => product.value?.variants ?? product.value?.productVariants ?? [],
);

// Galerie produit — déjà incluse dans products/get/{id} (même forme que
// GET /products/{id}/images), pas besoin d'un second appel juste pour
// l'afficher en lecture seule ici.
const gallery = computed<IProductGalleryImage[]>(() =>
  (product.value?.images ?? [])
    .slice()
    .sort(
      (a: IProductGalleryImage, b: IProductGalleryImage) =>
        a.displayOrder - b.displayOrder,
    ),
);

const extras = computed<any[]>(() => productExtras.value ?? []);

const TYPE_LABELS = computed<Record<string, string>>(() => ({
  food: t('product.typeFood'),
  'food&extra': t('product.typeFoodExtra'),
  extra: t('product.typeExtra'),
  plat: t('product.typeFood'),
  menu: t('product.typeMenu'),
}));

const PRICE_UNIT_LABELS = computed<Record<string, string>>(() => ({
  PER_PIECE: t('product.perPiece'),
  PER_KG: t('product.perKg'),
  PER_100G: t('product.per100g'),
  PER_LITER: t('product.perLiter'),
}));
</script>

<template>
  <div class="pdp">
    <!-- Breadcrumb -->
    <nav class="pdp__bc">
      <button class="pdp__bc-link" @click="goBack()">
        <i class="pi pi-arrow-left" />
        {{ fromId ? t('product.backToCommerce') : t('product.back') }}
      </button>
      <template v-if="fromId">
        <i class="pi pi-angle-right pdp__bc-sep" />
        <span class="pdp__bc-cur">{{ t('product.productDetail') }}</span>
      </template>
    </nav>

    <!-- Skeleton -->
    <template v-if="isPending">
      <div class="pdp__sk pdp__sk--hero" />
      <div class="pdp__sk pdp__sk--body" />
    </template>

    <!-- Erreur -->
    <div v-else-if="isError" class="pdp__load-error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('product.loadError') }}</p>
      <button class="pdp__btn pdp__btn--ghost" @click="goBack()">
        <i class="pi pi-arrow-left" /> {{ t('product.back') }}
      </button>
    </div>

    <template v-else-if="product">
      <!-- ══ HERO ══════════════════════════════════════════════════════════════ -->
      <div class="pdp__hero">
        <!-- Image -->
        <div class="pdp__hero-img-wrap">
          <img v-if="imgUrl" :src="imgUrl" class="pdp__hero-img" :alt="name" />
          <div v-else class="pdp__hero-img-ph"><i class="pi pi-box" /></div>
        </div>

        <!-- Info principale -->
        <div class="pdp__hero-main">
          <div class="pdp__hero-chips">
            <span class="pdp__chip pdp__chip--type">{{
              TYPE_LABELS[product.type] ?? product.type ?? t('product.typeFood')
            }}</span>
            <span
              class="pdp__chip"
              :class="isActive ? 'pdp__chip--active' : 'pdp__chip--off'"
            >
              <span class="pdp__chip-dot" />
              {{ isActive ? t('product.active') : t('product.inactive') }}
            </span>
            <span v-if="product.isRecommanded" class="pdp__chip pdp__chip--rec"
              ><i class="pi pi-thumbs-up" />
              {{ t('product.recommended') }}</span
            >
          </div>

          <h1 class="pdp__hero-name">{{ name }}</h1>
          <p v-if="desc" class="pdp__hero-desc">{{ desc }}</p>

          <div class="pdp__hero-price-row">
            <span class="pdp__hero-price">{{ fmtPrice(product.price) }}</span>
            <span v-if="product.priceUnit" class="pdp__hero-price-unit">{{
              PRICE_UNIT_LABELS[product.priceUnit] ?? product.priceUnit
            }}</span>
            <span v-if="product.packageUnit" class="pdp__hero-price-unit"
              >{{ t('product.soldBy') }} : {{ product.packageUnit
              }}<template v-if="product.packageQty">
                × {{ product.packageQty }}</template
              ></span
            >
          </div>

          <div class="pdp__hero-meta-row">
            <span v-if="product.preparationTime" class="pdp__meta-pill"
              ><i class="pi pi-clock" /> {{ product.preparationTime }} min</span
            >
            <span class="pdp__meta-pill"
              ><i class="pi pi-calendar" />
              {{ fmtDate(product.createdAt) }}</span
            >
            <span v-if="categories.length" class="pdp__meta-pill"
              ><i class="pi pi-tag" /> {{ categories.length }}
              {{
                categories.length > 1
                  ? t('product.catPlural')
                  : t('product.catSingular')
              }}</span
            >
            <span v-if="variants.length" class="pdp__meta-pill"
              ><i class="pi pi-list" /> {{ variants.length }}
              {{
                variants.length > 1
                  ? t('product.variantPlural')
                  : t('product.variantSingular')
              }}</span
            >
          </div>
        </div>

        <!-- Actions -->
        <div class="pdp__hero-actions">
          <button
            class="pdp__btn pdp__btn--primary"
            @click="
              router.push(
                `/products/${id}/edit${fromId ? '?from=' + fromId : ''}`,
              )
            "
          >
            <i class="pi pi-pencil" /> {{ t('product.edit') }}
          </button>
          <button
            class="pdp__btn pdp__btn--danger"
            @click="showDeleteModal = true"
          >
            <i class="pi pi-trash" /> {{ t('product.delete') }}
          </button>
        </div>
      </div>

      <!-- ══ BODY : 2 colonnes ═════════════════════════════════════════════════ -->
      <div class="pdp__body">
        <!-- Colonne gauche -->
        <div class="pdp__col">
          <!-- Détails -->
          <div class="pdp__card pdp__card--collapsible">
            <CollapsibleSection
              section-key="pdp-details"
              icon="pi-info-circle"
              :title="t('product.details')"
              default-open
            >
              <div class="pdp__kv-list">
                <div class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.price') }}</span
                  ><span class="pdp__kv-v pdp__kv-v--primary">{{
                    fmtPrice(product.price)
                  }}</span>
                </div>
                <div v-if="product.priceUnit" class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.priceUnit') }}</span
                  ><span class="pdp__kv-v">{{
                    PRICE_UNIT_LABELS[product.priceUnit] ?? product.priceUnit
                  }}</span>
                </div>
                <div v-if="product.packageUnit" class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.soldByLabel') }}</span
                  ><span class="pdp__kv-v"
                    >{{ product.packageUnit
                    }}<template v-if="product.packageQty">
                      × {{ product.packageQty }}</template
                    ></span
                  >
                </div>
                <div class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.type') }}</span
                  ><span class="pdp__kv-v">{{
                    TYPE_LABELS[product.type] ?? product.type ?? '—'
                  }}</span>
                </div>
                <div class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.status') }}</span>
                  <span
                    class="pdp__kv-v"
                    :class="
                      isActive ? 'pdp__kv-v--success' : 'pdp__kv-v--muted'
                    "
                    >{{
                      isActive ? t('product.active') : t('product.inactive')
                    }}</span
                  >
                </div>
                <div v-if="product.preparationTime" class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.prepTime') }}</span
                  ><span class="pdp__kv-v"
                    >{{ product.preparationTime }} min</span
                  >
                </div>
                <div v-if="product.isRecommanded" class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.isRecommended') }}</span
                  ><span class="pdp__kv-v pdp__kv-v--primary">{{
                    t('product.yes')
                  }}</span>
                </div>
                <div class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.createdAt') }}</span
                  ><span class="pdp__kv-v">{{
                    fmtDate(product.createdAt)
                  }}</span>
                </div>
                <div class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.updatedAt') }}</span
                  ><span class="pdp__kv-v">{{
                    fmtDate(product.updatedAt)
                  }}</span>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          <!-- Galerie — images secondaires, lecture seule (édition dans
           * /products/:id/edit, onglet Général). -->
          <div v-if="gallery.length" class="pdp__card pdp__card--collapsible">
            <CollapsibleSection
              section-key="pdp-gallery"
              icon="pi-images"
              title="Galerie"
              :subtitle="`${gallery.length} image(s)`"
              default-open
            >
              <ProductGalleryGrid
                :items="gallery"
                :pending-delete-id="null"
                :reordering="false"
                readonly
              />
            </CollapsibleSection>
          </div>

          <!-- Catégories -->
          <div class="pdp__card pdp__card--collapsible">
            <CollapsibleSection
              section-key="pdp-categories"
              icon="pi-folder"
              :title="t('product.categories')"
              default-open
            >
              <div v-if="categories.length" class="pdp__chips-wrap">
                <span
                  v-for="cat in categories"
                  :key="cat.id"
                  class="pdp__chip pdp__chip--cat"
                >
                  <i class="pi pi-tag" />
                  {{
                    cat.designationAlternatives?.fr ??
                    cat.designationAlternatives?.en ??
                    cat.designationAlternatives?.nl ??
                    cat.designation ??
                    cat.name ??
                    cat.id
                  }}
                </span>
              </div>
              <p v-else class="pdp__empty">{{ t('product.noCategories') }}</p>
            </CollapsibleSection>
          </div>

          <!-- TVA / Taxes -->
          <div
            v-if="product.taxes?.length"
            class="pdp__card pdp__card--collapsible"
          >
            <CollapsibleSection
              section-key="pdp-taxes"
              icon="pi-percentage"
              :title="t('product.taxes')"
              default-open
            >
              <div class="pdp__kv-list">
                <div
                  v-for="(tax, i) in product.taxes"
                  :key="tax.taxId ?? i"
                  class="pdp__kv"
                >
                  <span class="pdp__kv-k">{{ tax.name ?? tax.taxId }}</span
                  ><span class="pdp__kv-v"
                    >{{ tax.rate }}%
                    <span v-if="tax.country" class="pdp__variant-unit">{{
                      tax.country
                    }}</span></span
                  >
                </div>
              </div>
            </CollapsibleSection>
          </div>

          <!-- EAN -->
          <div v-if="product.ean?.length" class="pdp__card">
            <div class="pdp__card-hd">
              <i class="pi pi-barcode pdp__card-ico" /><span
                class="pdp__card-title"
                >{{ t('product.ean') }}</span
              >
            </div>
            <div class="pdp__chips-wrap">
              <span v-for="(code, i) in product.ean" :key="i" class="pdp__chip">
                {{ code }}
              </span>
            </div>
          </div>

          <!-- Commerce -->
          <div
            v-if="product.restaurant"
            class="pdp__card pdp__card--collapsible"
          >
            <CollapsibleSection
              section-key="pdp-store"
              icon="pi-shop"
              :title="t('product.store')"
              default-open
            >
              <div class="pdp__kv-list">
                <div class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.storeName') }}</span
                  ><span class="pdp__kv-v">{{
                    product.restaurant.designation
                  }}</span>
                </div>
                <div v-if="product.restaurant.address" class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.storeAddress') }}</span
                  ><span class="pdp__kv-v">{{
                    product.restaurant.address
                  }}</span>
                </div>
                <div v-if="product.restaurant.currencyCode" class="pdp__kv">
                  <span class="pdp__kv-k">{{ t('product.storeCurrency') }}</span
                  ><span class="pdp__kv-v">{{
                    product.restaurant.currencyCode
                  }}</span>
                </div>
              </div>
              <button
                v-if="fromId"
                class="pdp__btn pdp__btn--ghost pdp__btn--sm"
                @click="goBack()"
              >
                <i class="pi pi-arrow-right" /> {{ t('product.viewStore') }}
              </button>
            </CollapsibleSection>
          </div>

          <!-- Allergènes -->
          <div class="pdp__card pdp__card--collapsible">
            <CollapsibleSection
              section-key="pdp-allergens"
              icon="pi-shield"
              title="Allergènes"
              :subtitle="
                declaredAllergens.length
                  ? `${declaredAllergens.length} allergène(s) déclaré(s)`
                  : 'Aucun allergène déclaré'
              "
            >
              <div v-if="declaredAllergens.length" class="pdp__chips-wrap">
                <span
                  v-for="a in declaredAllergens"
                  :key="a.code"
                  class="pdp__chip"
                  :class="{ 'pdp__chip--req': a.presence === 'contains' }"
                >
                  {{ a.label }}
                  <template v-if="a.presence === 'may_contain'">
                    (traces)</template
                  >
                </span>
              </div>
              <p v-else class="pdp__empty-text">
                Aucun allergène n'a été déclaré pour ce produit.
              </p>
            </CollapsibleSection>
          </div>
        </div>

        <!-- Colonne droite -->
        <div class="pdp__col">
          <!-- Désignation multilingue -->
          <div class="pdp__card pdp__card--collapsible">
            <CollapsibleSection
              section-key="pdp-designation"
              icon="pi-language"
              :title="t('product.designation')"
              default-open
            >
              <div class="pdp__kv-list">
                <div class="pdp__kv">
                  <span class="pdp__kv-k"
                    ><span class="pdp__lang">FR</span></span
                  ><span class="pdp__kv-v">{{
                    designationTranslations.fr || '—'
                  }}</span>
                </div>
                <div class="pdp__kv">
                  <span class="pdp__kv-k"
                    ><span class="pdp__lang">EN</span></span
                  ><span class="pdp__kv-v">{{
                    designationTranslations.en || '—'
                  }}</span>
                </div>
                <div class="pdp__kv">
                  <span class="pdp__kv-k"
                    ><span class="pdp__lang">NL</span></span
                  ><span class="pdp__kv-v">{{
                    designationTranslations.nl || '—'
                  }}</span>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          <!-- Description -->
          <div v-if="desc" class="pdp__card pdp__card--collapsible">
            <CollapsibleSection
              section-key="pdp-description"
              icon="pi-align-left"
              :title="t('product.description')"
              default-open
            >
              <div class="pdp__kv-list">
                <div class="pdp__kv">
                  <span class="pdp__kv-k"
                    ><span class="pdp__lang">FR</span></span
                  ><span class="pdp__kv-v">{{
                    descriptionTranslations.fr || '—'
                  }}</span>
                </div>
                <div class="pdp__kv">
                  <span class="pdp__kv-k"
                    ><span class="pdp__lang">EN</span></span
                  ><span class="pdp__kv-v">{{
                    descriptionTranslations.en || '—'
                  }}</span>
                </div>
                <div class="pdp__kv">
                  <span class="pdp__kv-k"
                    ><span class="pdp__lang">NL</span></span
                  ><span class="pdp__kv-v">{{
                    descriptionTranslations.nl || '—'
                  }}</span>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          <!-- Variantes -->
          <div v-if="variants.length" class="pdp__card pdp__card--collapsible">
            <CollapsibleSection
              section-key="pdp-variants"
              icon="pi-list"
              :title="t('product.variants')"
              :subtitle="`${variants.length} variante(s)`"
              default-open
            >
              <div class="pdp__variants">
                <div v-for="v in variants" :key="v.id" class="pdp__variant">
                  <div class="pdp__variant-top">
                    <span class="pdp__variant-name">{{
                      v.labelAlternatives?.fr ||
                      v.labelAlternatives?.en ||
                      v.labelAlternatives?.nl ||
                      v.label?.fr ||
                      v.label?.en ||
                      (typeof v.label === 'string' ? v.label : '') ||
                      v.designation ||
                      '—'
                    }}</span>
                    <span
                      v-if="v.isDefault"
                      class="pdp__chip pdp__chip--default"
                      >{{ t('product.default') }}</span
                    >
                    <span
                      class="pdp__chip"
                      :class="
                        v.status === 'active'
                          ? 'pdp__chip--active'
                          : 'pdp__chip--off'
                      "
                      style="font-size: 0.6rem"
                    >
                      {{
                        v.status === 'active'
                          ? t('product.active')
                          : v.status === 'out-of-stock'
                            ? t('product.outOfStock')
                            : t('product.inactive')
                      }}
                    </span>
                  </div>
                  <div v-if="v.labelAlternatives" class="pdp__variant-i18n">
                    <span
                      v-if="v.labelAlternatives.en"
                      class="pdp__variant-i18n-item"
                      ><span class="pdp__lang">EN</span>
                      {{ v.labelAlternatives.en }}</span
                    >
                    <span
                      v-if="v.labelAlternatives.nl"
                      class="pdp__variant-i18n-item"
                      ><span class="pdp__lang">NL</span>
                      {{ v.labelAlternatives.nl }}</span
                    >
                  </div>
                  <div class="pdp__variant-meta">
                    <span class="pdp__variant-price">{{
                      fmtPrice(v.price)
                    }}</span>
                    <span v-if="v.priceUnit" class="pdp__variant-unit">{{
                      PRICE_UNIT_LABELS[v.priceUnit] ?? v.priceUnit
                    }}</span>
                    <span v-if="v.packageUnit" class="pdp__variant-unit"
                      >{{ v.packageUnit
                      }}<template v-if="v.packageQty">
                        × {{ v.packageQty }}</template
                      ></span
                    >
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          <!-- Groupes d'options (Extras) -->
          <div v-if="extras.length" class="pdp__card pdp__card--collapsible">
            <CollapsibleSection
              section-key="pdp-addon-groups"
              icon="pi-plus-circle"
              :title="t('product.addonGroups')"
              :subtitle="`${extras.length} groupe(s)`"
              default-open
            >
              <div class="pdp__extras">
                <div v-for="g in extras" :key="g.id" class="pdp__extra-group">
                  <div class="pdp__extra-group-hd">
                    <span class="pdp__extra-group-title">{{
                      g.title?.fr ||
                      g.title?.en ||
                      g.title?.nl ||
                      (typeof g.title === 'string' ? g.title : '') ||
                      g.designation?.fr ||
                      g.designation ||
                      '—'
                    }}</span>
                    <span v-if="g.required" class="pdp__chip pdp__chip--req">{{
                      t('product.required')
                    }}</span>
                    <span class="pdp__extra-group-type">{{
                      (g.chooseType ?? g.choiceType) === 'multiple'
                        ? t('product.multiple')
                        : t('product.unique')
                    }}</span>
                  </div>
                  <div
                    v-if="(g.option ?? g.options ?? g.addons ?? []).length"
                    class="pdp__extra-opts"
                  >
                    <span
                      v-for="o in g.option ?? g.options ?? g.addons ?? []"
                      :key="o.id"
                      class="pdp__extra-opt"
                    >
                      {{
                        o.content?.fr ||
                        o.content?.en ||
                        o.content?.nl ||
                        (typeof o.content === 'string' ? o.content : '') ||
                        o.designation ||
                        '—'
                      }}
                      <template v-if="o.price && parseFloat(o.price) > 0">
                        · +{{ fmtPrice(o.price) }}</template
                      >
                    </span>
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          </div>

          <!-- Promotions -->
          <div v-if="product.promotions?.length" class="pdp__card">
            <div class="pdp__card-hd">
              <i class="pi pi-percentage pdp__card-ico" /><span
                class="pdp__card-title"
                >{{ t('product.promotions') }}</span
              >
            </div>
            <div class="pdp__chips-wrap">
              <span
                v-for="(promo, i) in product.promotions"
                :key="i"
                class="pdp__chip pdp__chip--promo"
              >
                {{
                  promo?.designation ?? promo?.code ?? `Promo ${Number(i) + 1}`
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modale suppression -->
    <Teleport to="body">
      <div
        v-if="showDeleteModal"
        class="pdp__modal-backdrop"
        @click.self="showDeleteModal = false"
      >
        <div class="pdp__modal">
          <div class="pdp__modal-ico"><i class="pi pi-trash" /></div>
          <h2 class="pdp__modal-title">
            {{ t('product.deleteConfirmTitle') }}
          </h2>
          <p class="pdp__modal-text">
            {{ t('product.deleteConfirmBody') }}
          </p>
          <div class="pdp__modal-actions">
            <button
              class="pdp__btn pdp__btn--ghost"
              :disabled="deleting"
              @click="showDeleteModal = false"
            >
              {{ t('product.cancel') }}
            </button>
            <button
              class="pdp__btn pdp__btn--danger"
              :disabled="deleting"
              @click="confirmDelete"
            >
              <span v-if="deleting" class="pdp__spinner" />
              <i v-else class="pi pi-trash" />
              {{ deleting ? t('product.deleting') : t('product.delete') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.pdp {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .pdp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .pdp {
    padding: 1rem;
    gap: 1rem;
  }
}

/* Breadcrumb */
.pdp__bc {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.pdp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: color 0.15s;
}
.pdp__bc-link:hover {
  color: var(--nxt-primary);
}
[data-theme='dark'] .pdp__bc-link:hover {
  color: #ffd60a;
}
.pdp__bc-sep {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.pdp__bc-cur {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

/* Skeleton */
.pdp__sk {
  border-radius: 1rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: pdp-sk 1.4s infinite;
}
.pdp__sk--hero {
  height: 14rem;
}
.pdp__sk--body {
  height: 22rem;
}
@keyframes pdp-sk {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Error */
.pdp__load-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
}
.pdp__load-error i {
  font-size: 2rem;
  color: #ef4444;
}
.pdp__load-error p {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}

/* ── HERO ─────────────────────────────────────────────────────────────────── */
.pdp__hero {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1.25rem;
  padding: 1.75rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  flex-wrap: wrap;
}
@media (prefers-color-scheme: dark) {
  .pdp__hero {
    background: #1e2025;
    border-color: rgba(255, 255, 255, 0.07);
  }
}
[data-theme='dark'] .pdp__hero {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='light'] .pdp__hero {
  background: #fff;
  border-color: #ebebeb;
}

.pdp__hero-img-wrap {
  width: 10rem;
  height: 10rem;
  flex-shrink: 0;
  border-radius: 1rem;
  overflow: hidden;
  border: 1.5px solid #ebebeb;
  background: #f5f5f7;
  display: flex;
  align-items: center;
  justify-content: center;
}
[data-theme='dark'] .pdp__hero-img-wrap {
  border-color: rgba(255, 255, 255, 0.1);
  background: #2a2d34;
}
.pdp__hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.pdp__hero-img-ph {
  font-size: 3rem;
  color: #ccc;
}
[data-theme='dark'] .pdp__hero-img-ph {
  color: #444;
}

.pdp__hero-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.pdp__hero-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.pdp__hero-name {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #111;
  line-height: 1.2;
}
[data-theme='dark'] .pdp__hero-name {
  color: #f0f0f0;
}
.pdp__hero-desc {
  margin: 0;
  font-size: 0.875rem;
  color: #777;
  line-height: 1.55;
}
[data-theme='dark'] .pdp__hero-desc {
  color: #888;
}

.pdp__hero-price-row {
  display: flex;
  align-items: baseline;
  gap: 0.625rem;
  flex-wrap: wrap;
  margin-top: 0.125rem;
}
.pdp__hero-price {
  font-size: 1.75rem;
  font-weight: 900;
  color: var(--nxt-primary);
  letter-spacing: -0.03em;
}
[data-theme='dark'] .pdp__hero-price {
  color: #ffd60a;
}
.pdp__hero-price-unit {
  font-size: 0.8125rem;
  color: #999;
  font-weight: 500;
}
[data-theme='dark'] .pdp__hero-price-unit {
  color: #666;
}

.pdp__hero-meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.125rem;
}
.pdp__meta-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: #888;
  background: #f5f5f7;
  border: 1px solid #ebebeb;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
}
[data-theme='dark'] .pdp__meta-pill {
  background: #2a2d34;
  border-color: rgba(255, 255, 255, 0.08);
  color: #777;
}

.pdp__hero-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
}
@media (max-width: 640px) {
  .pdp__hero {
    flex-direction: column;
  }
  .pdp__hero-img-wrap {
    width: 100%;
    height: 12rem;
    border-radius: 0.875rem;
  }
  .pdp__hero-actions {
    flex-direction: row;
    width: 100%;
  }
  .pdp__hero-actions .pdp__btn {
    flex: 1;
    justify-content: center;
  }
}

/* ── BODY ─────────────────────────────────────────────────────────────────── */
.pdp__body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
@media (max-width: 768px) {
  .pdp__body {
    grid-template-columns: 1fr;
  }
}
.pdp__col {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Cards */
.pdp__card {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1rem;
  padding: 1.25rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.pdp__card--collapsible {
  padding: 0;
}
[data-theme='dark'] .pdp__card {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
}
[data-theme='light'] .pdp__card {
  background: #fff;
  border-color: #ebebeb;
}
.pdp__card-hd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
}
[data-theme='dark'] .pdp__card-hd {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}
.pdp__card-ico {
  font-size: 0.9375rem;
  color: var(--nxt-primary);
}
[data-theme='dark'] .pdp__card-ico {
  color: #ffd60a;
}
.pdp__card-title {
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 700;
  color: #111;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
[data-theme='dark'] .pdp__card-title {
  color: #f0f0f0;
}

/* KV */
.pdp__kv-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.pdp__kv {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.875rem;
}
.pdp__kv-k {
  color: #999;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}
[data-theme='dark'] .pdp__kv-k {
  color: #666;
}
.pdp__kv-v {
  color: #111;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}
[data-theme='dark'] .pdp__kv-v {
  color: #e0e0e0;
}
.pdp__kv-v--primary {
  color: var(--nxt-primary);
}
[data-theme='dark'] .pdp__kv-v--primary {
  color: #ffd60a;
}
.pdp__kv-v--success {
  color: #16a34a;
}
.pdp__kv-v--muted {
  color: #999;
}

/* Chips universels */
.pdp__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 1.6rem;
  padding: 0 0.6rem;
  border-radius: 0.35rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid transparent;
  white-space: nowrap;
}
.pdp__chip-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 50%;
  background: currentColor;
}
.pdp__chip--type {
  background: rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.2);
  color: var(--nxt-primary);
}
[data-theme='dark'] .pdp__chip--type {
  background: rgba(255, 214, 10, 0.12);
  border-color: rgba(255, 214, 10, 0.3);
  color: #ffd60a;
}
.pdp__chip--active {
  background: rgba(22, 163, 74, 0.08);
  border-color: rgba(22, 163, 74, 0.2);
  color: #16a34a;
}
.pdp__chip--off {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #9ca3af;
}
[data-theme='dark'] .pdp__chip--off {
  background: #2a2d34;
  border-color: rgba(255, 255, 255, 0.08);
}
.pdp__chip--rec {
  background: rgba(59, 130, 246, 0.07);
  border-color: rgba(59, 130, 246, 0.2);
  color: #3b82f6;
}
.pdp__chip--default {
  background: rgba(139, 92, 246, 0.08);
  border-color: rgba(139, 92, 246, 0.2);
  color: #8b5cf6;
}
.pdp__chip--req {
  background: rgba(239, 68, 68, 0.07);
  border-color: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}
.pdp__chip--cat {
  background: #f5f5f7;
  border-color: #ebebeb;
  color: #555;
  font-size: 0.7rem;
}
[data-theme='dark'] .pdp__chip--cat {
  background: #2a2d34;
  border-color: rgba(255, 255, 255, 0.08);
  color: #aaa;
}
.pdp__chip--promo {
  background: rgba(0, 0, 0, 0.06);
  border-color: rgba(0, 0, 0, 0.2);
  color: var(--nxt-primary);
}
[data-theme='dark'] .pdp__chip--promo {
  background: rgba(255, 214, 10, 0.12);
  border-color: rgba(255, 214, 10, 0.3);
  color: #ffd60a;
}
.pdp__chips-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

/* Count badge */
.pdp__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.375rem;
  height: 1.375rem;
  border-radius: 0.35rem;
  font-size: 0.6875rem;
  font-weight: 700;
  background: var(--nxt-surface-low, #f5f5f7);
  color: #888;
  border: 1px solid #ebebeb;
  padding: 0 0.25rem;
}
[data-theme='dark'] .pdp__count {
  background: #2a2d34;
  border-color: rgba(255, 255, 255, 0.08);
  color: #666;
}

/* Lang badges */
.pdp__lang {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.25rem;
  border-radius: 0.3rem;
  font-size: 0.6rem;
  font-weight: 800;
  background: #f0f0f0;
  color: #888;
  border: 1px solid #e0e0e0;
}
[data-theme='dark'] .pdp__lang {
  background: #2a2d34;
  border-color: rgba(255, 255, 255, 0.08);
  color: #666;
}

/* Misc */
.pdp__desc-text {
  margin: 0;
  font-size: 0.875rem;
  color: #777;
  line-height: 1.55;
}
[data-theme='dark'] .pdp__desc-text {
  color: #888;
}
.pdp__empty {
  margin: 0;
  font-size: 0.875rem;
  color: #aaa;
  font-style: italic;
}

/* Variantes */
.pdp__variants {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  max-height: 15.5rem;
  overflow-y: auto;
  padding-right: 0.25rem;
}
.pdp__variant {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
[data-theme='dark'] .pdp__variant {
  background: #23262d;
  border-color: rgba(255, 255, 255, 0.06);
}
.pdp__variant-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.pdp__variant-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111;
  flex: 1;
}
[data-theme='dark'] .pdp__variant-name {
  color: #e0e0e0;
}
.pdp__variant-i18n {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.pdp__variant-i18n-item {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: #666;
}
[data-theme='dark'] .pdp__variant-i18n-item {
  color: #aaa;
}
.pdp__variant-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.pdp__variant-price {
  font-size: 0.9375rem;
  font-weight: 800;
  color: var(--nxt-primary);
}
[data-theme='dark'] .pdp__variant-price {
  color: #ffd60a;
}
.pdp__variant-unit {
  font-size: 0.75rem;
  color: #aaa;
}

/* Extras */
.pdp__extras {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 17rem;
  overflow-y: auto;
  padding-right: 0.25rem;
}
.pdp__extra-group {
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
[data-theme='dark'] .pdp__extra-group {
  background: #23262d;
  border-color: rgba(255, 255, 255, 0.06);
}
.pdp__extra-group-hd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.pdp__extra-group-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111;
  flex: 1;
}
[data-theme='dark'] .pdp__extra-group-title {
  color: #e0e0e0;
}
.pdp__extra-group-type {
  font-size: 0.7rem;
  color: #aaa;
  margin-left: auto;
}
.pdp__extra-opts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}
.pdp__extra-opt {
  font-size: 0.75rem;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 0.3rem;
  padding: 0.2rem 0.55rem;
  color: #555;
}
[data-theme='dark'] .pdp__extra-opt {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
  color: #aaa;
}

/* Buttons */
.pdp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    opacity 0.15s,
    background 0.15s;
}
.pdp__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.pdp__btn--sm {
  height: 2rem;
  padding: 0 0.875rem;
  font-size: 0.8rem;
}
.pdp__btn--primary {
  background: var(
    --nxt-gradient-primary,
    linear-gradient(135deg, #333 0%, #111 100%)
  );
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
.pdp__btn--primary:not(:disabled):hover {
  filter: brightness(1.08);
}
[data-theme='dark'] .pdp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.3);
}
.pdp__btn--ghost {
  background: #f5f5f7;
  color: #555;
  border: 1px solid #e5e7eb;
}
[data-theme='dark'] .pdp__btn--ghost {
  background: #2a2d34;
  color: #aaa;
  border-color: rgba(255, 255, 255, 0.08);
}
.pdp__btn--ghost:hover {
  background: #ececec;
  color: #111;
}
[data-theme='dark'] .pdp__btn--ghost:hover {
  background: #333;
  color: #e0e0e0;
}
.pdp__btn--danger {
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.25);
}
.pdp__btn--danger:not(:disabled):hover {
  background: rgba(239, 68, 68, 0.14);
}

/* Spinner */
.pdp__spinner {
  width: 0.9rem;
  height: 0.9rem;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: pdp-spin 0.7s linear infinite;
}
@keyframes pdp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Modal */
.pdp__modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  backdrop-filter: blur(3px);
}
.pdp__modal {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 1.25rem;
  padding: 2rem 1.75rem;
  max-width: 28rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}
[data-theme='dark'] .pdp__modal {
  background: #1e2025;
  border-color: rgba(255, 255, 255, 0.07);
}
.pdp__modal-ico {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #ef4444;
}
.pdp__modal-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 800;
  color: #111;
}
[data-theme='dark'] .pdp__modal-title {
  color: #f0f0f0;
}
.pdp__modal-text {
  margin: 0;
  font-size: 0.875rem;
  color: #888;
  line-height: 1.5;
}
.pdp__modal-actions {
  display: flex;
  gap: 0.75rem;
  width: 100%;
}
.pdp__modal-actions .pdp__btn {
  flex: 1;
  justify-content: center;
}
</style>
